import { IslandType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen } from '@rta/rta-blueprint-library';

export type KitchenLayoutLabelVariant = 'body' | 'header';

export type GetKitchenLayoutLabelParams = {
	kitchen: Kitchen;
	variant?: KitchenLayoutLabelVariant;
};

/**
 * Builds the "Kitchen Layout" label that matches the PDF blueprint format.
 *
 * Base shape ← `kitchen.layout` enum (Linear / LShaped / UShaped / Galley / Custom),
 * plus a Side-by-Side geometry override. When the design contains a specialty-stand
 * island (Corner / Pizza / Asado / Smoker / Fire Pit) a ` w/ <Stand>` modifier is
 * appended — matching the finalized blueprints. Grill and Bar are primary → no modifier.
 *
 * The orientation-variant suffix ("Grill Right" / "Bar Left") is intentionally dropped.
 *
 * Two format variants:
 *   'body'   → overview "Kitchen Layout:" field, e.g. "L-Shape w/ Corner Stand".
 *   'header' → page-header title (CSS-uppercased), e.g. "L-Shape W/ Corner" → "L-SHAPE W/ CORNER".
 *
 * Rules:
 *   Base shape ← `kitchen.layout` enum. Canonical singular forms ("L-Shape", not "L-Shaped").
 *   Side-by-Side override (geometry-derived): 2+ islands, all Grill or Bar,
 *     all sharing the same `axis.y` rotation (parallel, same facing). Distinct
 *     from Galley, where the islands face each other (axis.y differs by 180°).
 */
const BASE_LABELS: Record<string, string> = {
	'linear': 'Linear',
	'l-shaped': 'L-Shape',
	'u-shaped': 'U-Shape',
	'galley': 'Galley',
	'custom': 'Custom'
};

// Specialty-stand modifier text. Header uses the bare stand keyword (uppercased by CSS);
// body uses the verbose stand name, matching the finalized "Kitchen Layout:" field.
const STAND_MODIFIERS: Partial<Record<IslandType, { header: string; body: string }>> = {
	[IslandType.Corner]: { header: 'Corner', body: 'Corner Stand' },
	[IslandType.Pizza]: { header: 'Pizza', body: 'Pizza Oven Stand' },
	[IslandType.Asado]: { header: 'Asado', body: 'Asado Stand' },
	[IslandType.Smoker]: { header: 'Smoker', body: 'Smoker Stand' },
	[IslandType.FirePit]: { header: 'Fire Pit', body: 'Fire Pit' }
};

// Normalize an axis.y rotation to [0, 360) so -90 and 270 compare equal.
const normalizeAngle = (angle: number): number => ((angle % 360) + 360) % 360;

const ORIGIN_TOLERANCE = 1;

const isSideBySide = (islands: Array<Island>): boolean => {
	// Side-by-Side is two parallel, same-facing Grill/Bar islands that are BUTTED TOGETHER.
	// More than two islands, or any non-Grill/Bar, is not an unambiguous Side-by-Side.
	if (islands.length !== 2) return false;

	const allGrillOrBar = islands.every((island) => island.type === IslandType.Grill || island.type === IslandType.Bar);
	if (!allGrillOrBar) return false;

	// Same-facing (parallel) — distinct from Galley, where the two islands oppose (axis.y differs 180°).
	const referenceAngle = normalizeAngle(islands[0].axis.y);
	if (!islands.every((island) => normalizeAngle(island.axis.y) === referenceAngle)) return false;

	// Adjacent: the pair must be connected (butted). A connection is a robust signal — real
	// side-by-side designs carry small depth/width offsets that an exact-coordinate check misses.
	return areConnected(islands[0], islands[1]);
};

/**
 * Geometry-derived layout key from island `axis.y` distribution — mirrors the library's
 * `getDescriptionCfn` classification. Used ONLY to recover the shape when the stored
 * `kitchen.layout` is a stale `custom`, so it must be conservative: it returns a template
 * key only when the geometry is UNAMBIGUOUSLY that template, and `null` otherwise (→ Custom).
 */
const areConnected = (a: Island, b: Island): boolean => {
	const conns = [...a.connections.getSourceConnections(), ...a.connections.getTargetConnections()];
	return conns.some((c) => c.sourceIsland?.id === b.id || c.targetIsland?.id === b.id);
};

// A true U-Shape is a CHAIN arm—base—arm: the one island connected to BOTH others (the base) must be
// perpendicular to both arms. This rejects a hub arrangement (an arm island joined to two others = a
// staggered Custom layout) even when the raw angles look U-like. Rotation-agnostic.
const isUShapeGeometry = (islands: Array<Island>): boolean => {
	if (islands.length !== 3) return false;
	return islands.some((base) => {
		const arms = islands.filter((island) => island.id !== base.id);
		if (!arms.every((arm) => areConnected(base, arm))) return false;
		const baseAngle = normalizeAngle(base.axis.y);
		return arms.every((arm) => {
			const delta = Math.abs(baseAngle - normalizeAngle(arm.axis.y)) % 360;
			return delta === 90 || delta === 270;
		});
	});
};

const deriveLayoutFromGeometry = (islands: Array<Island>): string | null => {
	const angles = islands.map((island) => normalizeAngle(island.axis.y));
	const unique = new Set(angles);
	const has = (angle: number): boolean => unique.has(angle);

	if (isUShapeGeometry(islands)) return 'u-shaped';
	// L-Shape: exactly two islands, one at 0 and one perpendicular.
	if (islands.length === 2 && has(0) && (has(90) || has(270)) && unique.size === 2) return 'l-shaped';
	// Galley: exactly two islands facing each other (0 and 180).
	if (islands.length === 2 && has(0) && has(180) && unique.size === 2) return 'galley';
	// Linear: a single island, or same-facing islands that sit in a straight line (shared depth).
	if (islands.length === 1) return 'linear';
	if (unique.size === 1 && islands.every((island) => Math.abs(island.origin.y - islands[0].origin.y) < ORIGIN_TOLERANCE)) return 'linear';
	return null;
};

// True when at least one island pair is perpendicular (axis.y differs by 90° or 270°).
const hasPerpendicularPair = (islands: Array<Island>): boolean => {
	const angles = islands.map((island) => normalizeAngle(island.axis.y));
	for (let i = 0; i < angles.length; i++) {
		for (let j = i + 1; j < angles.length; j++) {
			const delta = Math.abs(angles[i] - angles[j]) % 360;
			if (delta === 90 || delta === 270) return true;
		}
	}
	return false;
};

// True when at least one island pair opposes (axis.y differs by 180°).
const hasOpposingPair = (islands: Array<Island>): boolean => {
	const angles = islands.map((island) => normalizeAngle(island.axis.y));
	for (let i = 0; i < angles.length; i++) {
		for (let j = i + 1; j < angles.length; j++) {
			if (Math.abs(angles[i] - angles[j]) % 360 === 180) return true;
		}
	}
	return false;
};

/**
 * A stored template layout is trusted only when the island geometry actually supports it —
 * an L/U/Galley needs the corresponding perpendicular/opposing island relationship. A stored
 * template that contradicts the geometry (e.g. two parallel grills stored as `l-shaped`) is
 * stale and must fall through to the Custom classification.
 */
const geometrySupportsStoredLayout = (layout: string, islands: Array<Island>): boolean => {
	switch (layout) {
		case 'u-shaped':
			// A real U is a 3-island chain (arm–base–arm); a 2-island perpendicular pair stored as
			// u-shaped is stale and must re-derive to L-Shape.
			return isUShapeGeometry(islands);
		case 'l-shaped':
			return hasPerpendicularPair(islands);
		case 'galley':
			return hasOpposingPair(islands);
		case 'linear':
			// Stale if the geometry is a galley (opposing), L/U (perpendicular), or a butted Side-by-Side — re-derive.
			return !hasOpposingPair(islands) && !hasPerpendicularPair(islands) && !isSideBySide(islands);
		default:
			return true;
	}
};

const isStand = (island: Island): boolean => STAND_MODIFIERS[island.type] !== undefined;

/**
 * A named layout only exists when every specialty stand hangs off the END of the main
 * arrangement. A stand BETWEEN main islands (a smoker splitting two side-by-side grills)
 * has no name — the kitchen is Custom regardless of the stored layout. "Between" is
 * detected two ways: the stand bridges two mains by connection, or (when the mains are
 * parallel, the Linear/Side-by-Side family) the stand's origin.x falls inside the mains'
 * left-right span. Corner stands are exempt — they are joint connectors that legitimately
 * sit between the arms of an L/U.
 */
const standsInterruptMains = (mains: Array<Island>, stands: Array<Island>): boolean => {
	if (mains.length === 0 || stands.length === 0) return false;

	const mainAngles = new Set(mains.map((main) => normalizeAngle(main.axis.y)));
	const mainsParallel = mainAngles.size === 1;
	const xs = mains.map((main) => main.origin.x);
	const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];

	return stands.some((stand) => {
		if (stand.type === IslandType.Corner) return false;
		const connectedMains = mains.filter((main) => areConnected(stand, main));
		if (connectedMains.length >= 2) return true;
		return mainsParallel && mains.length >= 2 && stand.origin.x > minX && stand.origin.x < maxX;
	});
};

// ALL specialty-stand islands (deduped by type, canonical order) → the combined modifier. Matches
// the finalized: header "W/ ASADO & CORNER"; body "w/ Asado and Corner Stand" (a shared trailing
// "Stand" is collapsed onto the last stand only). Both variants use "w/" — never "with".
const getStandModifier = (islands: Array<Island>, variant: KitchenLayoutLabelVariant): string => {
	const seen = new Set<IslandType>();
	const mods = islands
		.filter((island) => STAND_MODIFIERS[island.type] && !seen.has(island.type) && seen.add(island.type))
		.map((island) => STAND_MODIFIERS[island.type]!);
	if (mods.length === 0) return '';

	if (variant === 'header') return ` W/ ${mods.map((m) => m.header).join(' & ')}`;

	const bodies = mods.map((m) => m.body);
	if (bodies.length === 1) return ` w/ ${bodies[0]}`;
	const leading = bodies.slice(0, -1).map((b) => b.replace(/ Stand$/, ''));
	return ` w/ ${[...leading, bodies[bodies.length - 1]].join(' and ')}`;
};

const getBaseLabel = (kitchen: Kitchen, islands: Array<Island>): string => {
	// Base SHAPE is derived from the main (Grill/Bar) islands only — specialty stands (Corner/
	// Asado/Pizza/etc.) are modifiers, not part of the L/U/Galley geometry, and their extra count
	// otherwise blocks classification (a 4-island L-with-stands reads as Custom).
	const mainIslands = islands.filter((island) => !isStand(island));
	const standIslands = islands.filter(isStand);
	const shapeIslands = mainIslands.length > 0 ? mainIslands : islands;

	// A stand wedged between the mains has no named layout — Custom, stored value or not.
	if (standsInterruptMains(mainIslands, standIslands)) return 'Custom';

	// Trust a stored template layout only when the geometry supports it; a template that
	// contradicts the geometry (stale) falls through to the Custom classification.
	const storedLabel = BASE_LABELS[kitchen.layout];
	if (storedLabel && kitchen.layout !== 'custom' && geometrySupportsStoredLayout(kitchen.layout, shapeIslands)) {
		return storedLabel;
	}

	// Stale/custom: only override when the geometry is unambiguously a template.
	if (isSideBySide(shapeIslands)) return 'Side-by-Side';

	const derivedKey = deriveLayoutFromGeometry(shapeIslands);
	return (derivedKey && BASE_LABELS[derivedKey]) ?? 'Custom';
};

export const getKitchenLayoutLabel = (params: GetKitchenLayoutLabelParams): string => {
	const { kitchen, variant = 'body' } = params;
	const { islands } = kitchen.design;

	return `${getBaseLabel(kitchen, islands.children)}${getStandModifier(islands.children, variant)}`;
};
